// ─── The screen that appears instead of a black one ──────────────────────────
//
// Farndale's `client/src/components/ErrorBoundary.tsx`, moved here on
// 2026-09-25. The mechanism is unchanged and is the point of the file:
// `getDerivedStateFromError` to stop the blank unmount,
// `componentDidCatch` to report it server-side, the stack on screen, and a
// reload button.
//
// ── Why app-ui and not the chassis ──────────────────────────────────────────
//
// This wraps the WHOLE tree, including the chassis's own shell. Putting the
// thing that catches the shell crashing inside the shell is the wrong way
// round — and Crafting Comb uses `@nulife/app-ui` without the chassis, so it
// gets the fix here and would not have there.
//
// ── The two adaptations, and why they are not reinvention ───────────────────
//
// 1. COLOURS. Farndale's version styles from shadcn classes — `bg-background`,
//    `text-destructive`, `bg-muted`, `bg-primary`. Those come from the Mira
//    chassis's own `index.css` and do not exist anywhere in this platform, so
//    a literal copy would render an unreadable box of undefined colours at the
//    exact moment somebody needs to read it. They map to the `--nl-*` tokens
//    every other shared component here uses.
// 2. ICONS. Farndale draws two `lucide-react` icons. That package is not a
//    dependency of this platform and adding one for two glyphs would be a
//    worse trade than using `⚠`, which is what `DataState` and `Notice`
//    already use for exactly this meaning.
//
// Neither touches what the component DOES. Inline styles rather than utility
// classes for the same reason `app-ui`'s other pieces use them: this has to
// render correctly in an app that has not loaded the chassis stylesheet, and
// the case where it is needed most is the case where something is wrong.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError } from "./reportClientError.js";

type Props = {
  children: ReactNode;
  /** Names which app crashed, so one server log serves several of them. */
  appName?: string;
};

type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // The whole tree has just unmounted. This report is the only record that
    // it happened at all — without it the person sees a blank screen, closes
    // the tab, and there is nothing to look at afterwards. That is not a
    // hypothetical: it is what happened to Memorial Hall on 2026-09-24,
    // mid-demo, and why this file is shared rather than Farndale's alone.
    reportClientError("ErrorBoundary", error, {
      componentStack: errorInfo.componentStack,
      app: this.props.appName,
      // What was on screen when it went. A stale bundle against a restarted
      // server looks exactly like this and is otherwise unprovable.
      url: typeof window === "undefined" ? undefined : window.location.pathname,
    });
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        data-testid="crash-screen"
        style={{
          minHeight: "100vh", padding: 24,
          display: "flex", alignItems: "flex-start", justifyContent: "center",
          background: "var(--nl-bg)", color: "var(--nl-ink)",
        }}
      >
        <div style={{ width: "100%", maxWidth: 680, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span aria-hidden style={{ fontSize: 24 }}>⚠</span>
            <h2 style={{ margin: 0, fontSize: "1.2rem" }}>Something went wrong on this screen.</h2>
          </div>

          {/* Said plainly. Somebody reading this is usually mid-task and needs
              to know whether their work survived, and the honest answer is
              that this crashed before it could be saved. */}
          <p style={{ margin: 0, color: "var(--nl-ink-dim)" }}>
            It has been reported. Anything you had just typed on this screen was
            not saved. Reloading usually fixes it — if it happens again in the
            same place, that is worth telling somebody.
          </p>

          <div>
            <button
              type="button"
              data-testid="crash-reload"
              onClick={() => window.location.reload()}
              style={{
                minHeight: "var(--nl-hit-min)", padding: "0 16px", fontWeight: 600,
                borderRadius: "var(--nl-radius-sm)",
                border: "1px solid var(--nl-band-stroke)",
                background: "var(--nl-band)", color: "var(--nl-band-ink)",
                font: "inherit", cursor: "pointer",
              }}
            >Reload the page</button>
          </div>

          {/* The stack stays on screen, as Farndale shows it. This is an
              internal committee tool, not a public site: the person looking at
              it is far more likely to be able to send a screenshot that helps
              than to be harmed by seeing a stack trace. */}
          <details data-testid="crash-detail">
            <summary style={{ cursor: "pointer", color: "var(--nl-ink-dim)" }}>
              What went wrong
            </summary>
            <pre
              style={{
                marginTop: 8, padding: 12, overflow: "auto", maxHeight: 320,
                borderRadius: "var(--nl-radius-sm)",
                background: "var(--nl-bg-raised)",
                border: "1px solid var(--nl-stroke)",
                color: "var(--nl-ink-dim)",
                fontSize: "0.8rem", whiteSpace: "break-spaces",
              }}
            >{error.stack ?? error.message}</pre>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
